import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  accountStatusDisplay,
  invitationStateDisplay,
  membershipRoleDisplay,
  membershipStatusDisplay,
  relatedMembershipsLinks,
  type MembershipListRow,
  type MembershipSortKey,
} from "../lib/memberships-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: MembershipSortKey;
  onSortChange: (value: MembershipSortKey) => void;
  organizationLabel: string;
};

export function MembershipsFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
  organizationLabel,
}: FilterBarProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "active", label: "عضویت فعال" },
    { id: "invited", label: "دعوت‌شده" },
    { id: "suspended", label: "معلق" },
    { id: "removed", label: "حذف‌شده" },
    { id: "account_inactive", label: "حساب غیرفعال" },
    { id: "tenant_admin", label: "مدیر سازمان" },
    { id: "publisher", label: "ناشر" },
    { id: "editor", label: "ویرایشگر" },
    { id: "auditor", label: "ممیز" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر اعضای سازمان"
    >
      <p className="sites-inventory__muted" style={{ width: "100%" }}>
        دامنه سازمان فعال:{" "}
        <strong>
          <LtrIsolate mono>{organizationLabel}</LtrIsolate>
        </strong>
      </p>
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر اعضای سازمان"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی عضو</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="نام، ایمیل، نقش…"
          aria-label="جستجوی اعضای سازمان"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as MembershipSortKey)}
          aria-label="مرتب‌سازی فهرست اعضای سازمان"
        >
          <option value="name">نام</option>
          <option value="email">ایمیل</option>
          <option value="role">نقش</option>
          <option value="status">وضعیت عضویت</option>
          <option value="updated">آخرین به‌روزرسانی</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

export function MembershipInspectSummary({
  item,
  organizationLabel,
  isSelf,
  actions,
}: {
  item: MembershipListRow;
  organizationLabel: string;
  isSelf: boolean;
  actions?: ReactNode;
}) {
  const role = membershipRoleDisplay(item.role);
  const membership = membershipStatusDisplay(item.status);
  const account = accountStatusDisplay(item.userActive);
  const invitation = invitationStateDisplay(item);
  const links = relatedMembershipsLinks();

  return (
    <section className="content-inspect" aria-labelledby="membership-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">اعضای سازمان</p>
          <h2 id="membership-inspect-title" className="content-inspect__name">
            {item.displayName}
            {isSelf ? " (شما)" : ""}
          </h2>
          <p className="content-inspect__id">
            عضویت <LtrIsolate mono>{item.id}</LtrIsolate>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="نقش و وضعیت‌ها">
          <span className={`state-badge ${TONE_BADGE[role.tone]}`}>{role.label}</span>
          <span className={`state-badge ${TONE_BADGE[membership.tone]}`}>{membership.label}</span>
          <span className={`state-badge ${TONE_BADGE[account.tone]}`}>{account.label}</span>
          <span className={`state-badge ${TONE_BADGE[invitation.tone]}`}>{invitation.label}</span>
        </div>
      </header>

      {membership.detail && (
        <p className="content-inspect__eligibility-detail">{membership.detail}</p>
      )}
      {account.detail && <p className="content-inspect__eligibility-detail">{account.detail}</p>}
      {invitation.detail && (
        <p className="content-inspect__eligibility-detail">{invitation.detail}</p>
      )}
      {role.detail && <p className="content-inspect__eligibility-detail">{role.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>ایمیل</dt>
          <dd>
            <LtrIsolate mono>{item.email}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>نقش سازمانی</dt>
          <dd>
            {role.label} (<LtrIsolate mono>{item.role}</LtrIsolate>)
          </dd>
        </div>
        <div>
          <dt>وضعیت عضویت</dt>
          <dd>{membership.label}</dd>
        </div>
        <div>
          <dt>وضعیت حساب ورود</dt>
          <dd>{account.label}</dd>
        </div>
        <div>
          <dt>وضعیت دعوت</dt>
          <dd>{invitation.label}</dd>
        </div>
        <div>
          <dt>دامنه سازمان</dt>
          <dd>
            <LtrIsolate mono>{organizationLabel}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>شناسه کاربر</dt>
          <dd>
            <LtrIsolate mono>{item.userId}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>دعوت</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.invitedAt)}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>فعال‌سازی</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.activatedAt)}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>به‌روزرسانی</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.updatedAt)}</LtrIsolate>
          </dd>
        </div>
      </dl>

      <nav className="content-inspect__links" aria-label="مسیرهای مرتبط مدیریت">
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

export function MembershipsInventoryList({
  rows,
  selectedId,
  currentUserId,
  onSelect,
  renderActions,
}: {
  rows: MembershipListRow[];
  selectedId: string | null;
  currentUserId: string | undefined;
  onSelect: (id: string) => void;
  renderActions: (row: MembershipListRow) => ReactNode;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول اعضای سازمان"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">نام</th>
              <th scope="col">ایمیل</th>
              <th scope="col">نقش</th>
              <th scope="col">وضعیت عضویت</th>
              <th scope="col">حساب</th>
              <th scope="col">دعوت / فعال‌سازی</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const role = membershipRoleDisplay(row.role);
              const membership = membershipStatusDisplay(row.status);
              const account = accountStatusDisplay(row.userActive);
              const invitation = invitationStateDisplay(row);
              const isSelf = row.userId === currentUserId;
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      {row.displayName}
                      {isSelf ? " (شما)" : ""}
                    </button>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.email}>
                      <LtrIsolate mono>{row.email}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[role.tone]}`}>{role.label}</span>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={row.role}
                    >
                      <LtrIsolate mono>{row.role}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[membership.tone]}`}>
                      {membership.label}
                    </span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[account.tone]}`}>
                      {account.label}
                    </span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[invitation.tone]}`}>
                      {invitation.label}
                    </span>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={formatKabulDisplay(row.updatedAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.updatedAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های اعضای سازمان"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const role = membershipRoleDisplay(row.role);
          const membership = membershipStatusDisplay(row.status);
          const account = accountStatusDisplay(row.userActive);
          const isSelf = row.userId === currentUserId;
          return (
            <li
              key={row.id}
              className={`sites-inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  {row.displayName}
                  {isSelf ? " (شما)" : ""}
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[role.tone]}`}>{role.label}</span>
                  <span className={`state-badge ${TONE_BADGE[membership.tone]}`}>
                    {membership.label}
                  </span>
                  <span className={`state-badge ${TONE_BADGE[account.tone]}`}>
                    {account.label}
                  </span>
                </span>
                <span className="sites-inventory__muted">
                  <LtrIsolate mono>{row.email}</LtrIsolate>
                </span>
                <span className="sites-inventory__muted">
                  نقش فنی: <LtrIsolate mono>{row.role}</LtrIsolate>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
